import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { FaUserPlus, FaEdit, FaEye, FaTrash } from "react-icons/fa";
import { getToken } from "../../api/auth";
import {
  getAllUsers,
  addUser,
  editUser,
  changePassword,
  deleteUser,
  getUserRoles,
} from "../../api/user";
import "./Agents.css";

const Agents = () => {
  const token = getToken();
  const [agents, setAgents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userCompanyCode, setUserCompanyCode] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    companyCode: "",
    password: "",
    name: "",
    email: "",
    phone: "",
    entryDate: new Date().toISOString().split("T")[0],
    roleId: 2, // Default to Order Controller
    isActive: true, // Added isActive with default true
  });
  const [editUsername, setEditUsername] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [viewAgent, setViewAgent] = useState(null);
  const [changePasswordAgent, setChangePasswordAgent] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const users = await getAllUsers();
        // Get the logged-in user's company code
        const loggedInUser = users[0]; // All users have same company code
        if (loggedInUser && loggedInUser.companyCode) {
          setUserCompanyCode(loggedInUser.companyCode);
        }
        // Filter users to only include roleId 2, 3, or 4
        const filteredUsers = users.filter((user) =>
          [2, 3, 4].includes(Number(user.roleId))
        );
        setAgents(filteredUsers);
        const rolesData = await getUserRoles();
        // Filter roles to only include roleId 2, 3, or 4
        const filteredRoles = rolesData.filter((role) =>
          [2, 3, 4].includes(Number(role.id))
        );
        setRoles(filteredRoles);
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
      }
    };
    if (token) {
      fetchData();
    }
  }, [token]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.id === Number(roleId));
    return role ? role.name : "Unknown";
  };

  const filteredAgents = agents.filter((agent) => {
    const matchesSearch =
      agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole =
      roleFilter === "All" || Number(agent.roleId).toString() === roleFilter;
    return matchesSearch && matchesRole;
  });

  const openModal = (agent = null) => {
    setFormData(
      agent
        ? { ...agent, password: "" } // Clear password for edit
        : {
            username: "",
            companyCode: userCompanyCode,
            password: "",
            name: "",
            email: "",
            phone: "",
            entryDate: new Date().toISOString().split("T")[0],
            roleId: 2, // Default to Order Controller
            isActive: true, // Added isActive for new agents
          }
    );
    setEditUsername(agent ? agent.username : null);
    setIsModalOpen(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (![2, 3, 4].includes(Number(formData.roleId))) {
        setError(
          "Invalid role selected. Please choose Order Controller, Warehouse Agent, or Driver."
        );
        return;
      }

      const userData = {
        ...formData,
        roleId: Number(formData.roleId), // ensure number
        entryDate: formData.entryDate.split("T")[0], // ensure YYYY-MM-DD
      };

      let response;

      if (editUsername) {
        // Update existing user (without password)
        response = await editUser(editUsername, userData);
      } else {
        // Create new user
        if (!userData.password) {
          setError("Password is required for new users.");
          return;
        }
        response = await addUser(userData);
      }

      // Show success message from API
      const successMessage = response?.message || "Agent saved successfully.";
      setError(null);
      setSuccess(successMessage);

      // Refresh the agents list by fetching all users
      const users = await getAllUsers();
      const filteredUsers = users.filter((user) =>
        [2, 3, 4].includes(Number(user.roleId))
      );
      setAgents(filteredUsers);

      // Reset form
      setIsModalOpen(false);
      setFormData({
        username: "",
        companyCode: userCompanyCode,
        password: "",
        name: "",
        email: "",
        phone: "",
        entryDate: new Date().toISOString().split("T")[0],
        roleId: 2,
        isActive: true,
      });
      setEditUsername(null);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.Password?.[0] ||
        err.response?.data?.message ||
        `Failed to ${editUsername ? "update" : "add"} agent. Please try again.`;
      setError(errorMessage);
    }
  };

  const handleDelete = async (username) => {
    try {
      const agent = agents.find((a) => a.username === username);

      if (!agent) {
        setError("Agent not found.");
        return;
      }

      if ([2, 3, 4].includes(Number(agent.roleId))) {
        await deleteUser(agent.username);
        setAgents(agents.filter((a) => a.username !== username));
        setShowDeleteConfirm(null);
      } else {
        setError(
          "Cannot delete this agent. Only Order Controllers, Warehouse Agents, and Drivers can be deleted."
        );
      }
    } catch (err) {
      console.error("Delete error:", err);
      setError("Failed to delete agent. Please try again.");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      if (!newPassword || newPassword.length < 6) {
        setError("Password must be at least 6 characters long.");
        return;
      }

      // Send full user data with new password
      const userData = {
        ...changePasswordAgent,
        password: newPassword,
        roleId: Number(changePasswordAgent.roleId),
        entryDate: changePasswordAgent.entryDate.split("T")[0],
      };

      const response = await changePassword(userData);

      // Show success message from API
      const successMessage =
        response?.message || "Password changed successfully.";
      setError(null);
      setSuccess(successMessage);

      // Close modal and reset
      setChangePasswordAgent(null);
      setNewPassword("");

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.Password?.[0] ||
        err.response?.data?.message ||
        "Failed to change password. Please try again.";
      setError(errorMessage);
    }
  };

  return (
    <div className="agents-container">
      <div className="floating-bg floating-bg-1"></div>
      <div className="floating-bg floating-bg-2"></div>
      <div className="floating-bg floating-bg-3"></div>
      <div className="page-header">
        <h2 className="agents-title">Warehouse Agents</h2>
        <p className="page-subtitle">Manage your warehouse team members</p>
      </div>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      <div className="filters">
        <input
          type="text"
          placeholder="Search by name or username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="All">All Roles</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
        <button className="glass-btn add-agent-btn" onClick={() => openModal()}>
          <FaUserPlus size={20} />
          Add Agent
        </button>
      </div>
      <div className="agents-grid">
        {filteredAgents.map((agent) => (
          <div key={agent.username} className="agent-card">
            <div className="agent-card-header">
              <div className="agent-initials">{agent.name[0]}</div>
              <div className="agent-basic-info">
                <h3 className="agent-name">{agent.name}</h3>
                <p className="agent-id">Username: {agent.username}</p>
              </div>
            </div>
            <div className="agent-card-body">
              <div className="agent-detail-row">
                <span className="detail-label">Role:</span>
                <span className="detail-value">
                  {getRoleName(agent.roleId)}
                </span>
              </div>
              <div className="agent-detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{agent.email}</span>
              </div>
              <div className="agent-detail-row">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{agent.phone}</span>
              </div>
              <div className="agent-detail-row">
                <span className="detail-label">Joined:</span>
                <span className="detail-value">
                  {new Date(agent.entryDate).toLocaleDateString()}
                </span>
              </div>
              <div className="agent-detail-row">
                <span className="detail-label">Company Code:</span>
                <span className="detail-value">{agent.companyCode}</span>
              </div>
              <div className="agent-detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value">
                  {agent.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            <div className="agent-card-actions">
              <button
                className="glass-btn btn-secondary"
                onClick={() => openModal(agent)}
              >
                <FaEdit size={16} /> Edit
              </button>
              <button
                className="glass-btn btn-primary"
                onClick={() => setViewAgent(agent)}
              >
                <FaEye size={16} /> View Details
              </button>
              <button
                className="glass-btn btn-delete"
                onClick={() => setShowDeleteConfirm(agent.username)}
              >
                <FaTrash size={16} /> Delete
              </button>
            </div>
          </div>
        ))}
        {filteredAgents.length === 0 && (
          <div className="empty-state">
            <p>No agents found</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editUsername ? "Edit Agent" : "Add Agent"}</h3>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  required
                />
              </div>
              {!editUsername && (
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required
                  />
                </div>
              )}
              {editUsername && (
                <div className="form-group">
                  <label>Password</label>
                  <button
                    type="button"
                    className="glass-btn change-password-btn"
                    onClick={() => {
                      setChangePasswordAgent(formData);
                      setIsModalOpen(false);
                    }}
                  >
                    Change Password
                  </button>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="roleId">Role</label>
                <select
                  id="roleId"
                  value={formData.roleId}
                  onChange={(e) =>
                    setFormData({ ...formData, roleId: Number(e.target.value) })
                  }
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="entryDate">Join Date</label>
                <input
                  type="date"
                  id="entryDate"
                  value={formData.entryDate}
                  onChange={(e) =>
                    setFormData({ ...formData, entryDate: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="companyCode">Company Code</label>
                <input
                  type="text"
                  id="companyCode"
                  value={formData.companyCode}
                  disabled
                  className="input-disabled"
                />
              </div>
              <div className="form-group">
                <label htmlFor="isActive">Active Status</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label htmlFor="isActive" className="toggle-label">
                    <span className="toggle-text">
                      {formData.isActive ? "Active" : "Inactive"}
                    </span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="glass-btn cancel-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="glass-btn submit-btn">
                  {editUsername ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this agent?</p>
            {error && <div className="error-message">{error}</div>}
            <div className="modal-actions">
              <button
                className="glass-btn cancel-btn"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="glass-btn delete-btn"
                onClick={() => handleDelete(showDeleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {viewAgent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Agent Details</h3>
            <div className="agent-view-details">
              <div className="detail-section">
                <h4>Personal Information</h4>
                <div className="detail-row">
                  <span className="label">Name:</span>
                  <span className="value">{viewAgent.name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Username:</span>
                  <span className="value">{viewAgent.username}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Email:</span>
                  <span className="value">{viewAgent.email}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Phone:</span>
                  <span className="value">{viewAgent.phone}</span>
                </div>
              </div>
              <div className="detail-section">
                <h4>Job Information</h4>
                <div className="detail-row">
                  <span className="label">Role:</span>
                  <span className="value">{getRoleName(viewAgent.roleId)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Join Date:</span>
                  <span className="value">
                    {new Date(viewAgent.entryDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Company Code:</span>
                  <span className="value">{viewAgent.companyCode}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className="value status">
                    <span
                      className={viewAgent.isActive ? "active" : "inactive"}
                    >
                      {viewAgent.isActive ? "Active" : "Inactive"}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="glass-btn cancel-btn"
                onClick={() => setViewAgent(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {changePasswordAgent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Change Password</h3>
            <p className="modal-subtitle">
              Changing password for: <strong>{changePasswordAgent.name}</strong>
            </p>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="glass-btn cancel-btn"
                  onClick={() => {
                    setChangePasswordAgent(null);
                    setNewPassword("");
                    setError(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="glass-btn submit-btn">
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;
